<template>
  <div class="hot-jobs-section">
    <div class="section-header">
      <h2 class="section-title">热招岗位</h2>
      <slot name="extra"></slot>
    </div>

    <div v-if="loading" class="loading-state">
      <img src="@/assets/image/loading.png" alt="loading" />
    </div>

    <div v-else class="job-grid">
      <div
        v-for="job in jobs"
        :key="job.id || job.job_id"
        class="job-card"
        @click="handleJobClick(job)"
      >
        <div class="job-card-header">
          <div class="title-wrapper">
            <h3 class="job-title" :title="job.job_title || job.title">
              {{ job.job_title || job.title || job.position_name }}
            </h3>
            <span v-if="job.recruitment_type" class="badge">
              {{ getRecuitmenntLabel(job.recruitment_type) }}
            </span>
          </div>
          <div class="job-salary">
            {{ formatSalary(job.salary_start, job.salary_end, job.salary_month) || job.salary || '面议' }}
          </div>
        </div>

        <div class="job-meta">
          <span>{{ job.work_nature || '全职' }}</span>
          <span>{{ getEducationLabel(job.education) }}</span>
          <span>{{ getExperienceText(job.experience) }}</span>
          <span v-if="job.job_tags && job.job_tags.length">{{ job.job_tags[0] }}</span>
        </div>

        <div class="company-info">
          <div class="company-left">
            <div class="company-row">
              <img v-if="job.company_logo" :src="job.company_logo" :alt="job.company_name" class="company_logo"/> 
              <div v-else class="company-logo-placeholder">
                {{ getCompanyNamePrefix(job.company_name) }}
              </div>
              <div>
                <div class="company-name" :title="job.company_name">
                  {{ job.company_name || '—' }}
                </div>
                <div class="company-desc" :title="job.category">
                  {{ job.category || '—' }}
                </div>
              </div>
            </div>
          </div>
          <div class="job-location" :title="job.city || job.job_address">
            {{ job.city || job.job_address || '—' }}
          </div>
        </div>
      </div>
    </div>

    <div class="view-more-wrapper">
      <button class="view-more-btn" @click="$emit('view-more')">查看更多岗位</button>
    </div>
  </div>
</template>

<script>
import { getHotJobRecommend } from '@/api/recommend'
import { getEducationLabel, getRecuitmenntLabel, getExperienceText, formatSalary } from '@/constants/jobConstants'

export default {
  name: 'HotJobGrid',
  props: {
    limit: { type: Number, default: 9 },
    requestParams: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      jobs: [],
      loading: false
    }
  },
  created() {
    this.fetchHotJobs()
  },
  methods: {
    getEducationLabel,
    getRecuitmenntLabel,
    getExperienceText(value) {
      return getExperienceText(value) || '经验不限'
    },
    formatSalary,
    getCompanyNamePrefix(companyName) {
      if (!companyName) return '公司'
      // 获取公司名的前四个字
      return companyName.substring(0, 4) || '公司'
    },
    handleJobClick(job) {
      const jobId = job.id || job.job_id
      if (jobId) {
        this.$router.push({
          path: '/hot-jobs',
          query: { job_id: jobId }
        })
      }
    },
    async fetchHotJobs() {
      if (this.loading) return
      this.loading = true
      try {
        // 构建只包含需要字段的查询参数
        const allowedFields = [
          'keyword',
          'city',
          'category',
          'education',
          'recruitment_type',
          'salary_min',
          'salary_max'
        ]
        
        // 从 requestParams 中只提取白名单字段
        const baseParams = allowedFields.reduce((acc, field) => {
          if (field in this.requestParams) {
            const value = this.requestParams[field]
            // 只添加有值的字段
            if (value !== '' && value !== null && value !== undefined) {
              // salary_min 和 salary_max 为 0 时表示不限，可以不发送
              if (field === 'salary_min' || field === 'salary_max') {
                if (value !== 0) {
                  acc[field] = value
                }
              } else {
                acc[field] = value
              }
            }
          }
          return acc
        }, {})
        
        const res = await getHotJobRecommend(baseParams)
        if (res?.data?.code === 1) {
          const lists = res.data.data?.lists || res.data.data || []
          this.jobs = lists.slice(0, this.limit)
        } else {
          this.jobs = []
          console.error('获取热招岗位失败:', res?.data?.msg)
        }
      } catch (error) {
        console.error('获取热招岗位异常:', error)
        this.jobs = []
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
.hot-jobs-section {
  width: 100%;
  padding: 32px 0;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
}

.section-title {
  font-size: 36px;
  background: linear-gradient(90deg, #62AFFF 0%, #9C41FF 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0;
}

.loading-state {
  text-align: center;
  img {
    width: 360px;
  }
}

.job-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.job-card {
  background: rgba(255, 255, 255, 0.4);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 12px 32px rgba(118, 94, 228, 0.15);
  backdrop-filter: blur(18px);
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    border-color: rgba(124, 65, 255, 0.4);
    box-shadow: 0 18px 40px rgba(124, 65, 255, 0.2);
  }
}

.job-card-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.title-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.job-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.badge {
  padding: 2px 8px;
  border-radius: 6px;
  background: rgba(124, 65, 255, 0.1);
  color: #7C41FF;
  font-size: 12px;
  flex-shrink: 0;
}

.job-salary {
  font-size: 18px;
  font-weight: 600;
  background: linear-gradient(90deg, #7EB4EB 0%, #B16EF9 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  flex-shrink: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.job-meta {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 12px;
  color: #656587;

  span {
    padding: 2px 8px;
    border-radius: 6px;
    background: rgba(101, 101, 135, 0.1);
  }
}

.company-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.company-left {
  min-width: 0;
}

.company-name {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.company-desc {
  font-size: 12px;
  color: #888;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.job-location {
  font-size: 12px;
  color: #656587;
  flex-shrink: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 36px;
}

.view-more-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 24px;
}

.view-more-btn {
  padding: 8px 98px;
  border-radius: 4px;
  border: none;
  background: linear-gradient(90deg, #62AFFF 0%, #9C41FF 100%);
  color: #fff;
  font-size: 14px;
  cursor: pointer;
}
.company-row{
  display: flex;
  gap: 8px;
  align-items: center;
}

.company_logo{
  width: 34px;
  height: 34px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
}

.company-logo-placeholder {
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 500;
  color: #666;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  border-radius: 4px;
}
@media (max-width: 1200px) {
  .job-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .job-grid {
    grid-template-columns: 1fr;
  }

  .job-card {
    padding: 16px;
  }

  .job-card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .title-wrapper {
    width: 100%;
  }

  .job-title {
    max-width: 100%;
    font-size: 15px;
  }

  .job-salary {
    font-size: 16px;
    width: 100%;
    max-width: 100%;
    text-align: left;
  }

  .job-meta {
    font-size: 11px;
    gap: 6px;
  }

  .company-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .job-location {
    width: 100%;
    text-align: left;
  }
}

@media (max-width: 480px) {
  .job-card {
    padding: 12px;
  }

  .job-title {
    font-size: 14px;
  }

  .job-salary {
    font-size: 15px;
  }

  .company-name {
    font-size: 13px;
  }

  .company-desc {
    font-size: 11px;
  }

  .job-location {
    font-size: 11px;
  }
}
</style>
